type PageItem = { type: 'page'; page: number } | { type: 'break'; key: string };

interface UsePaginationOptions {
  pageCount: number;
  currentPage: number; // 1-indexed
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
}

const usePagination = ({
  pageCount,
  currentPage,
  pageRangeDisplayed = 7,
  marginPagesDisplayed = 3,
}: UsePaginationOptions): PageItem[] => {
  if (pageCount <= 1) return [];

  const visible = new Set<number>();

  // Start margin
  for (let i = 1; i <= Math.min(marginPagesDisplayed, pageCount); i++) {
    visible.add(i);
  }

  // End margin
  for (
    let i = Math.max(pageCount - marginPagesDisplayed + 1, 1);
    i <= pageCount;
    i++
  ) {
    visible.add(i);
  }

  // Range around current page
  const half = Math.floor(pageRangeDisplayed / 2);
  const rangeStart = Math.max(1, currentPage - half);
  const rangeEnd = Math.min(pageCount, rangeStart + pageRangeDisplayed - 1);
  for (let i = rangeStart; i <= rangeEnd; i++) {
    visible.add(i);
  }

  const sorted = Array.from(visible).sort((a, b) => a - b);
  const items: PageItem[] = [];
  let prev = 0;

  for (const page of sorted) {
    if (page - prev > 1) {
      items.push({ type: 'break', key: `break-${prev}-${page}` });
    }
    items.push({ type: 'page', page });
    prev = page;
  }

  return items;
};

export default usePagination;
